import React, { useEffect, useRef, type FC } from 'react'

import { useUserContext } from '@logic/contexts/AppStore/UserContext.tsx'
import { usePolicyContext } from '@logic/contexts/AppStore/AbacContext.tsx'

import { StudentOutlineIcon } from '@icons/index.ts'
import styles from './LearnerShow.module.scss'

import { useLearnerShow } from '@logic/contexts/Learners/LearnerShowContext.tsx'
import '@styles/common.scss'

import { ShowBase } from 'react-admin'
import { LearnerShowTabs } from '@src/pages/Learner/Show/LearnerShowTabs.tsx'
import { LxIcon } from '@components/icon/Icon.tsx'
import { useLocation } from 'react-router-dom'
import { LxLoadingSpinner } from '@components/loader/loadingSpinner.tsx'
import { cn } from '@src/utils/cn.ts'
import { LxHandleKeyDown } from '@src/utils/Accessbility'
import { checkAccSettingsScope } from '@src/utils/accountSettings'
import { useModal } from '@src/logic/contexts/Modal/ModalContext'
import { PollModal } from '@src/pages/Poll/PollModal.tsx'
import { findFirstValidPoll } from '@src/pages/Poll/DashboardPoll'

export const LearnerShowHeader: FC = () => {
  const { currentUser } = useUserContext() as any
  const { canPerformAction } = usePolicyContext()
  const { learner, onEditProfile, isLoading } = useLearnerShow()
  const { showModal } = useModal()

  const can_edit_learner = canPerformAction(currentUser, { type: 'Learner' }, 'edit')

  // Add the RBAC check for privacy notice
  const { canAccess: canSeePrivacyMessage, effectiveValue } = checkAccSettingsScope('privacy_notice')
  let accHasPrivacyMessage = effectiveValue === 'true'

  const pageRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Focus the page when navigated
    pageRef.current?.focus()
  }, [])

  useEffect(() => {
    if (isLoading || !learner) return

    const currentLearnerPolls = learner.assignedPolls as any
    const firstPollEntry = findFirstValidPoll(currentLearnerPolls)

    if (firstPollEntry) {
      const [pollId, poll] = firstPollEntry
      showModal(<PollModal poll={poll} pollId={pollId} resourceId={learner.id} pollScope='LEARNER' backToPath={`/Learner/${learner.id}/show/goal`} />)
    }
  }, [isLoading, learner])

  return (
    <div className={styles.studentProfileHeader} ref={pageRef} tabIndex={-1} aria-label='Learner Profile' style={{ outline: 'none' }}>
      {canSeePrivacyMessage && accHasPrivacyMessage && (
        <div className={cn(styles.onlyOnPrint, styles.confidentialBanner)}>
          <p style={{ fontSize: '16px', textAlign: 'left', lineHeight: '24px', marginBottom: '10px' }}>
            <strong>Privacy Notice:</strong> This report contains confidential student or child information. Use only as permitted by your organization’s policies. Unauthorized access, sharing, or
            misuse is prohibited and may violate applicable laws and policies.
          </p>
        </div>
      )}

      <div className={styles.studentName}>
        <LxIcon icon={StudentOutlineIcon} sxStyles={{ height: '80px', width: '80px' }}></LxIcon>
        <span className={styles.title}> Learner: </span>
        <span className={styles.boldStudentName}> {learner?.fullName}</span>
      </div>
      <div className={cn(styles.studentActions, 'ignoreOnPrint')}>
        {can_edit_learner && (
          <div
            className={cn('lxActionButton lxActionButtonDefaultSize')}
            onClick={onEditProfile}
            tabIndex={0}
            onKeyDown={(event) => LxHandleKeyDown(event, onEditProfile)}
            role='link'
            aria-label='Edit Learner Info'
          >
            <span>Edit learner info</span>{' '}
          </div>
        )}
      </div>
    </div>
  )
}

export const LearnerShow = () => {
  const { isLoading } = useLearnerShow()
  const location = useLocation()

  // Check if the current URL path includes '/members'
  if (location.pathname.includes('/members') || location.pathname.includes('/edit')) {
    return null
  }

  return (
    <ShowBase>
      {isLoading ? (
        <LxLoadingSpinner />
      ) : (
        <>
          <LearnerShowHeader></LearnerShowHeader>
          <LearnerShowTabs></LearnerShowTabs>
        </>
      )}
    </ShowBase>
  )
}

export default LearnerShow
